import { useEffect } from "react";
import DOMPurify from "dompurify";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/lib/i18n";

const sanitize = (html: string) =>
  DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

const memCache = new Map<string, string>();
function loadCache(lang: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(`hvi_tr_${lang}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveCache(lang: string, map: Record<string, string>) {
  try {
    localStorage.setItem(`hvi_tr_${lang}`, JSON.stringify(map));
  } catch {}
}

// Block-level selectors we translate as whole units (preserves inline <strong>/<em>/<a>).
const BLOCK_SELECTOR =
  "p, li, h1, h2, h3, h4, h5, h6, summary, blockquote, figcaption, dd, dt, " +
  "span.label-caps, [data-translate-block]";

// Also translate leaf elements that contain only text (no children) and aren't already inside a block above.
function isTextOnlyLeaf(el: Element): boolean {
  if (el.children.length > 0) return false;
  const t = (el.textContent ?? "").trim();
  if (t.length < 2) return false;
  if (!/[A-Za-zÀ-ÿ]/.test(t)) return false;
  return true;
}

// Minimum text length (chars) to send to AI translation. Kept low so short
// UI labels (buttons, headings, captions) are also translated. Results are
// cached per-language in localStorage, so the cost is one-time.
const MIN_AI_TRANSLATE_LEN = 4;

function shouldTranslate(s: string): boolean {
  const t = s.trim();
  if (t.length < MIN_AI_TRANSLATE_LEN) return false;
  if (!/[A-Za-zÀ-ÿ]/.test(t)) return false;
  if (/^https?:\/\//i.test(t)) return false;
  return true;
}

const SKIP_INSIDE = "script, style, code, pre, noscript, [data-no-translate]";

/**
 * Walks the DOM under `rootSelector` and translates each block-level element
 * as a whole unit (HTML preserved) using translate-batch. Cached per-language
 * in localStorage. Re-runs when lang or dep change.
 */
export function useDomTranslate(
  rootSelector: string,
  lang: Lang,
  dep: unknown = null,
) {
  useEffect(() => {
    if (lang === "en") return;
    const root = document.querySelector(rootSelector);
    if (!root) return;
    let cancelled = false;
    const disk = loadCache(lang);

    // Collect target elements
    const seen = new Set<Element>();
    const targets: Element[] = [];

    root.querySelectorAll(BLOCK_SELECTOR).forEach((el) => {
      if (el.closest(SKIP_INSIDE)) return;
      // Skip if a parent block is already in targets (avoid double-translating nested li inside p)
      let p: Element | null = el.parentElement;
      while (p && p !== root) {
        if (seen.has(p)) return;
        p = p.parentElement;
      }
      if (!shouldTranslate(el.textContent ?? "")) return;
      seen.add(el);
      targets.push(el);
    });

    // Add text-only leafs (e.g. <span> with bare text) not inside an already-collected block
    root.querySelectorAll("span, div, strong, em, a, button").forEach((el) => {
      if (el.closest(SKIP_INSIDE)) return;
      if (!isTextOnlyLeaf(el)) return;
      let p: Element | null = el.parentElement;
      let insideTarget = false;
      while (p && p !== root) {
        if (seen.has(p)) {
          insideTarget = true;
          break;
        }
        p = p.parentElement;
      }
      if (insideTarget) return;
      if (!shouldTranslate(el.textContent ?? "")) return;
      seen.add(el);
      targets.push(el);
    });

    // Apply cached + collect missing
    const missingSet = new Map<string, Element[]>();
    for (const el of targets) {
      const key = el.innerHTML.trim();
      const cached = memCache.get(`${lang}::${key}`) ?? disk[key];
      if (cached) {
        const safe = sanitize(cached);
        if (el.innerHTML !== safe) el.innerHTML = safe;
      } else {
        if (!missingSet.has(key)) missingSet.set(key, []);
        missingSet.get(key)!.push(el);
      }
    }

    const missing = Array.from(missingSet.keys());
    if (missing.length === 0) return;

    (async () => {
      const CHUNK = 8;
      const chunks: string[][] = [];
      for (let s = 0; s < missing.length; s += CHUNK) {
        chunks.push(missing.slice(s, s + CHUNK));
      }
      await Promise.all(
        chunks.map(async (chunk) => {
          try {
            const { data, error } = await supabase.functions.invoke("translate-batch", {
              body: { texts: chunk, target: lang, html: true },
            });
            if (error || cancelled) return;
            const translations: string[] = (data as any)?.translations ?? chunk;
            translations.forEach((tr, k) => {
              const original = chunk[k];
              if (typeof tr !== "string" || !tr) return;
              const safeTr = sanitize(tr);
              disk[original] = safeTr;
              memCache.set(`${lang}::${original}`, safeTr);
              const els = missingSet.get(original) ?? [];
              for (const el of els) {
                if (el.innerHTML.trim() === original) el.innerHTML = safeTr;
              }
            });
          } catch {
            // ignore
          }
        }),
      );
      saveCache(lang, disk);
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, rootSelector, dep]);
}
